import React, { useState } from "react";
import { FaEye, FaEyeSlash, FaCog } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AdvertiserChangePassword = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [step, setStep] = useState(1); // step 1 = old password, step 2 = new password fields
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);

  // Step 1: Verify old password
  const handleVerifyOldPassword = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/user/password/verify-old`,
        { oldPassword },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Verify Old Password Response:", res);
      if (res.status === 200) {
        toast.success("Old password verified successfully.");
        setStep(2);
        setMessage("");
      } else {
        toast.error("Old password is incorrect.");
        setMessage("Old password is incorrect.");
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Old password is incorrect.");
        setMessage("Old password is incorrect.");
      } else {
        toast.error("Error verifying old password.");
        setMessage("Error verifying old password.");
      }
    }
    setLoading(false);
  };

  // Step 2: Change password
  const handleChangePassword = async () => {
    if (newPassword !== confirmNewPassword) {
      toast.error("New Password and Confirm Password do not match!");
      setMessage("New Password and Confirm Password do not match!");
      return;
    }
    setLoading(true);
    setShowApproveSpinner(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/user/password/update`,
        { newPassword, confirmNewPassword },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      console.log("Change New Password Response:", res);
      if (res.status === 200) {
        toast.success("Password changed successfully!");
        setMessage("Password changed successfully!");
        setStep(1);
        setOldPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
      } else {
        toast.error("Failed to change password.");
        setMessage("Failed to change password.");
      }
    } catch (error) {
      toast.error("Error changing password.");
      setMessage("Error changing password.");
    }
    setLoading(false);
    setShowApproveSpinner(false);
  };

  return (
    <div className="flex flex-col items-center justify-start min-h-[calc(100vh-120px)] bg-transparent pt-4 px-3 sm:px-6">
      <div className="w-full max-w-lg bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-slate-100 overflow-hidden animate-in fade-in zoom-in duration-500">
        {/* Header Section */}
        <div className="bg-slate-900 p-8 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-rose-500/10 rounded-full -ml-12 -mb-12 blur-2xl" />

          <div className="w-14 h-14 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/20 shadow-xl">
            <FaCog className="text-white text-2xl animate-[spin_8s_linear_infinite]" />
          </div>
          <h2 className="text-2xl font-black text-white tracking-tight">Security Portal</h2>
          <p className="text-slate-400 text-xs font-bold uppercase tracking-[0.2em] mt-1">Update your credentials</p>
        </div>

        {/* Content Section */}
        <div className="p-6 sm:p-10 space-y-6">
          {/* Progress Indicator */}
          <div className="flex items-center gap-2 mb-2">
            <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${step >= 1 ? 'bg-indigo-600' : 'bg-slate-100'}`} />
            <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${step >= 2 ? 'bg-indigo-600' : 'bg-slate-100'}`} />
          </div>

          <div className="animate-in slide-in-from-right-4 duration-500">
            {step === 1 && (
              <div className="space-y-5">
                <div>
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                    Old Password Verify
                  </label>
                  <div className="relative group">
                    <input
                      type={showOldPassword ? "text" : "password"}
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold focus:bg-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-600 transition-colors"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                    >
                      {showOldPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleVerifyOldPassword}
                  disabled={loading || !oldPassword}
                  className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-slate-200 hover:bg-indigo-600 hover:shadow-indigo-200 active:scale-95 transition-all disabled:opacity-50 disabled:active:scale-100"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Verifying</span>
                    </div>
                  ) : "Verify Identity"}
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5 animate-in slide-in-from-right-4 duration-500">
                <div>
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold focus:bg-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none"
                      placeholder="Minimum 8 characters"
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-600"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
                    Confirm Secret
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmNewPassword ? "text" : "password"}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold focus:bg-white focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none"
                      placeholder="Repeat new password"
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-600"
                      onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                    >
                      {showConfirmNewPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={handleChangePassword}
                    disabled={loading || !newPassword || !confirmNewPassword}
                    className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-slate-900 hover:shadow-slate-200 active:scale-95 transition-all"
                  >
                    {loading ? "Updating System..." : "Update Password"}
                  </button>
                  <button
                    onClick={() => setStep(1)}
                    className="text-[11px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-900 transition-colors mx-auto"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modern Overlay Spinner */}
      {showApproveSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm z-[100] p-6">
          <div className="bg-white p-8 rounded-[2.5rem] shadow-2xl border border-slate-100 flex flex-col items-center text-center animate-in zoom-in duration-300">
            <div className="relative w-16 h-16 mb-6">
              <div className="absolute inset-0 border-4 border-indigo-100 rounded-full" />
              <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center">
                  <FaCog className="text-indigo-600 animate-pulse" />
                </div>
              </div>
            </div>
            <h3 className="text-lg font-black text-slate-900">Synchronizing</h3>
            <p className="text-slate-500 text-xs font-bold mt-1">Applying new security protocols...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertiserChangePassword;
